import {
  Alert,
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import DetailModal from './detail-modal';
import { License, Specialty, Timeslot } from '../../../generated/graphql';
import ScheduleModal from './schedule-modal';
import { providerTitleInPatientState } from '../../../shared/licenses-to-eligible-states';
import { Icon } from '../../icon/icon';
import { Specialties } from './specialties';
import { formatProviderNameWithCredential } from '../../../helpers/format-helpers';
import { months } from '../../../helpers/day';

export type ProviderSubsectionProps = {
  firstName?: string;
  lastName?: string;
  photo?: string;
  biography?: string;
  primaryCredential?: string;
  specialties: Specialty[];
  licenses?: License[];
  providerTimeslots?: Timeslot[];
  patientAddressState?: string;
  shouldShowCSDisclaimer: boolean;
  selectProvider: (appointmentTime?: string) => Promise<{ status: string }>;
  wasProviderAdditionalMatch?: boolean;
};

export const ProviderSubsection = (props: ProviderSubsectionProps) => {
  const {
    firstName,
    lastName,
    photo,
    primaryCredential,
    licenses,
    providerTimeslots,
    patientAddressState,
    shouldShowCSDisclaimer,
    wasProviderAdditionalMatch,
  } = props;
  // TAP-1647: If provider does not have an MD or DO, then they have an NP or PMHNP license, making them a clinician
  const providerType = providerTitleInPatientState(
    licenses,
    patientAddressState
  );
  const getCardSubtitle = () => {
    const subtitle = providerType;
    return subtitle.charAt(0).toUpperCase() + subtitle.slice(1);
  };

  const earliestStartTime = providerTimeslots[0].startTime;
  const formattedTime = format(new Date(earliestStartTime), 'hh:mm a');

  const formattedMonth = months[new Date(earliestStartTime).getMonth()];
  const day = new Date(earliestStartTime).getDate();

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        avatar={
          <Avatar
            src={photo}
            alt={lastName}
            sx={{ width: '56px', height: '56px' }}
          />
        }
        title={formatProviderNameWithCredential(
          firstName,
          lastName,
          primaryCredential
        )}
        subheader={getCardSubtitle()}
        sx={{ pl: 2, pt: 2, pb: 0 }}
      />
      <DetailModal
        wasProviderAdditionalMatch={wasProviderAdditionalMatch}
        {...props}
      />
      <CardContent sx={{ pt: 1 }}>
        {shouldShowCSDisclaimer && (
          <Alert
            severity="warning"
            icon={<Icon icon="Shield" />}
            sx={{ mb: 2 }}
          >
            This {providerType} cannot prescribe stimulants or benzodiazepines
            in your state due to local regulations.
          </Alert>
        )}
        <Specialties {...props} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            <ScheduleModal
              wasProviderAdditionalMatch={wasProviderAdditionalMatch}
              {...props}
            />
          </Grid>
        </Grid>
        <Typography variant="caption">
          Earliest availability is on {formattedMonth} {day} at {formattedTime}
        </Typography>
      </CardContent>
    </Card>
  );
};
